/**
 * SaasCameraList — §2 "Linear/Vercel professional tool" cameras renderer.
 *
 * Dense hairline list: 64px rounded thumbnail (latest detection image via
 * CameraThumbnail URL — uses latest_detection_thumbnail directly), name +
 * IMEI stacked tight, existing badge/pill components right-aligned (reusing
 * OnlineStatusBadge + IngestionTypeBadge), inline last-event/last-seen time,
 * actions menu on hover (opacity-0 group-hover:opacity-100).
 *
 * Selection checkbox left when selectionMode active.
 *
 * data-testid="saas-camera-row" on each row.
 */

import { useState } from 'react'
import {
  MoreVertical,
  Trash2,
  Settings,
  MapPin,
  Signal,
  Camera as CameraIcon,
  ImageOff,
  Loader2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { OnlineStatusBadge } from '@/components/cameras/OnlineStatusBadge'
import { IngestionTypeBadge } from '@/components/cameras/IngestionTypeBadge'
import { formatRelativeTimeShort } from '@/lib/utils'
import type { Camera } from '@/types'

// ── sub-components ─────────────────────────────────────────────────────────────

function SaasThumb({ src, alt }: { src: string | null; alt: string }) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>(
    src ? 'loading' : 'error',
  )
  return (
    <div className="relative h-[64px] w-[64px] flex-shrink-0 overflow-hidden rounded bg-muted">
      {src && status !== 'error' ? (
        <>
          {status === 'loading' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/40" />
            </div>
          )}
          <img
            src={src}
            alt={alt}
            className={`h-full w-full object-cover transition-opacity duration-200 ${
              status === 'loaded' ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setStatus('loaded')}
            onError={() => setStatus('error')}
          />
        </>
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          {src === null ? (
            <CameraIcon className="h-5 w-5 text-muted-foreground/25" />
          ) : (
            <ImageOff className="h-5 w-5 text-muted-foreground/25" />
          )}
        </div>
      )}
    </div>
  )
}

function SelectCheckbox({ selected }: { selected: boolean }) {
  return (
    <div
      className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${
        selected ? 'bg-primary border-primary' : 'border-muted-foreground/40'
      }`}
    >
      {selected && (
        <svg viewBox="0 0 8 8" className="h-2.5 w-2.5 fill-primary-foreground">
          <path d="M1 4l2 2 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      )}
    </div>
  )
}

// ── row ────────────────────────────────────────────────────────────────────────

interface SaasRowProps {
  camera: Camera
  selected: boolean
  selectionMode: boolean
  onToggleSelect: (id: number) => void
  onDeleteRequest: (camera: Camera) => void
  onEditRequest: (camera: Camera) => void
  onMapRequest: (camera: Camera) => void
  onSimRequest: (camera: Camera) => void
  simUsageMB?: number | null
}

function SaasRow({
  camera,
  selected,
  selectionMode,
  onToggleSelect,
  onDeleteRequest,
  onEditRequest,
  onMapRequest,
  onSimRequest,
  simUsageMB,
}: SaasRowProps) {
  const navigate = useNavigate()
  const { t } = useTranslation('cameras')

  const handleClick = () => {
    if (selectionMode) { onToggleSelect(camera.id); return }
    if (camera.is_locked) {
      toast.error('Camera locked', { description: 'SIM mismatch — under review. Please contact support.' })
      return
    }
    navigate(`/events?camera=${camera.id}`)
  }

  return (
    <div
      data-testid="saas-camera-row"
      className={`text-[13px] border-b border-border hover:bg-muted px-2 py-2 flex items-center gap-3 group cursor-pointer transition-colors duration-100 ${
        selected ? 'bg-primary/5' : ''
      }`}
      onClick={handleClick}
    >
      {/* Selection checkbox */}
      {selectionMode && (
        <div onClick={(e) => { e.stopPropagation(); onToggleSelect(camera.id) }}>
          <SelectCheckbox selected={selected} />
        </div>
      )}

      {/* 64px thumbnail */}
      <SaasThumb src={camera.latest_detection_thumbnail} alt={camera.name} />

      {/* Name + IMEI stacked */}
      <div className="flex-1 min-w-0">
        <p className="font-medium leading-tight truncate">{camera.name}</p>
        <p className="text-[11px] tabular-nums text-muted-foreground truncate">
          {camera.imei}
        </p>
      </div>

      {/* Pills right-aligned */}
      <div className="flex items-center gap-2 flex-shrink-0 hidden sm:flex">
        <IngestionTypeBadge type={camera.ingestion_type} />
        <OnlineStatusBadge isOnline={camera.is_online} lastSeen={camera.last_seen} />
      </div>

      {/* Last contact time */}
      <span className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap flex-shrink-0 hidden md:block">
        {camera.last_seen
          ? formatRelativeTimeShort(camera.last_seen)
          : '—'}
      </span>

      {/* Actions — visible on hover */}
      {!selectionMode && (
        <div
          className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-[120ms] flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            title={t('card.cameraSettings')}
            onClick={() => onEditRequest(camera)}
          >
            <Settings className="h-3.5 w-3.5" />
          </Button>
          {camera.latitude && camera.longitude && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              title={t('card.viewOnMap')}
              onClick={() => onMapRequest(camera)}
            >
              <MapPin className="h-3.5 w-3.5" />
            </Button>
          )}
          {camera.iccid && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              title={t('card.simDetails')}
              onClick={() => onSimRequest(camera)}
            >
              <Signal className="h-3.5 w-3.5" />
              {simUsageMB != null && (
                <span className="ml-0.5 text-[10px]">
                  {simUsageMB >= 1024
                    ? `${(simUsageMB / 1024).toFixed(1)}G`
                    : `${Math.round(simUsageMB)}M`}
                </span>
              )}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground">
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive focus:bg-destructive/10"
                onClick={() => onDeleteRequest(camera)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('card.deleteCamera')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  )
}

// ── main component ─────────────────────────────────────────────────────────────

export interface SaasCameraListHandlers {
  onToggleSelect: (id: number) => void
  onDeleteRequest: (camera: Camera) => void
  onEditRequest: (camera: Camera) => void
  onMapRequest: (camera: Camera) => void
  onSimRequest: (camera: Camera) => void
}

export interface SaasCameraListProps extends SaasCameraListHandlers {
  cameras: Camera[]
  selectionMode: boolean
  selectedIds: Set<number>
  simUsageMap: Record<string, { monthUsedMB: number }>
}

export function SaasCameraList({
  cameras,
  selectionMode,
  selectedIds,
  simUsageMap,
  onToggleSelect,
  onDeleteRequest,
  onEditRequest,
  onMapRequest,
  onSimRequest,
}: SaasCameraListProps) {
  return (
    <div data-testid="saas-camera-list" className="rounded border border-border overflow-hidden">
      {cameras.map((camera) => (
        <SaasRow
          key={camera.id}
          camera={camera}
          selected={selectedIds.has(camera.id)}
          selectionMode={selectionMode}
          onToggleSelect={onToggleSelect}
          onDeleteRequest={onDeleteRequest}
          onEditRequest={onEditRequest}
          onMapRequest={onMapRequest}
          onSimRequest={onSimRequest}
          simUsageMB={
            camera.iccid && simUsageMap[camera.iccid]
              ? simUsageMap[camera.iccid].monthUsedMB
              : null
          }
        />
      ))}
    </div>
  )
}
